import { GripVertical, Plus, X } from "lucide-react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from "@hello-pangea/dnd";
import {
  IgButton,
  IgIconButton,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { ExperienceCustomMetricDto } from "@intelligems/ig-types";
import { officialCategoryName } from "@intelligems/ig-utils";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import { CustomEventsDrawer } from "@src/features/customEvents/CustomEventsDrawer";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { CustomEventsEsmAlert } from "@src/providers/alerts/AlertBanner";
import { initCustomEvent } from "@src/store/slices/customEvents";
import { isDrawerOpen, openDrawer } from "@src/store/slices/drawers";
import {
  getCustomMetrics,
  getKeyMetrics,
  removeCustomMetric,
  reorderCustomMetrics,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { theme } from "@src/theme";
import { mpClickAddMetric } from "@src/utils/mixpanel";

export const ConfigureCustomMetrics = () => {
  const dispatch = useDispatch();
  const { isReadOnly } = useReadOnlyMode();
  const org = useGetOrg();
  const scriptStatus = useGetScriptStatus(
    org.data?.org.id,
    undefined,
    undefined,
    undefined,
    {
      enabled: !isReadOnly,
    }
  );
  const isCustomDrawerOpen = useSelector(isDrawerOpen("customEvents"));
  const experienceCustomMetrics = useSelector(getCustomMetrics);
  const experienceKeyMetrics = useSelector(getKeyMetrics);
  const experience = useSelector(selectedExperience);
  const [hover, setHover] = useImmer(-1);
  const onDragEnd = (result: DropResult) => {
    if (result.destination) {
      dispatch(
        reorderCustomMetrics({
          startIndex: result.source.index,
          endIndex: result.destination.index,
        })
      );
    }
  };

  const handleRemoveCustomMetric = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    // removing the custom metric handles removing/re-ordering the key metrics
    dispatch(
      removeCustomMetric({
        indexToRemove: index,
      })
    );
  };

  return (
    <IgPaper>
      <Stack spacing={2}>
        <IgTypography variant={"h2"} sx={{ fontWeight: 600 }}>
          Choose Custom Metrics
        </IgTypography>
        <IgTypography sx={{ color: theme.palette.text.secondary }}>
          Compare and filter results by clicks, page visits, and custom
          javascript behavior. These must be chosen before you start your{" "}
          {officialCategoryName(experience.category, false)}.
        </IgTypography>
        {!isReadOnly && scriptStatus.data?.type === "isCjs" && (
          <Grid marginTop={"10px"}>
            <CustomEventsEsmAlert orgId={org.data?.org?.id} />
          </Grid>
        )}
        <Grid>
          {isReadOnly && experienceCustomMetrics.length === 0 ? (
            <IgTypography sx={{ color: theme.palette.text.secondary }}>
              No custom metrics configured
            </IgTypography>
          ) : (
            <>
              {/* @ts-ignore */}
              <DragDropContext onDragEnd={onDragEnd}>
                {/* @ts-ignore */}
                <Droppable
                  droppableId={"custom-metric-zone"}
                  direction={"vertical"}
                >
                  {(provided: DroppableProvided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {experienceCustomMetrics.map(
                        (customMetric, index: number) => (
                          // @ts-ignore
                          <Draggable
                            key={`key-${customMetric.id}`}
                            draggableId={`${customMetric.id}`}
                            index={index}
                            isDragDisabled={
                              isReadOnly || experienceCustomMetrics.length === 1
                            }
                          >
                            {(
                              dragProvided: DraggableProvided,
                              dragSnapshot: DraggableStateSnapshot
                            ) => {
                              let transform =
                                dragProvided.draggableProps.style!.transform;

                              if (dragSnapshot.isDragging && transform) {
                                transform = transform.replace(/\(.+,/, "(0,");
                              }

                              const style = {
                                ...dragProvided.draggableProps.style,
                                transform,
                              };
                              return (
                                <Grid
                                  key={`key-inner-${customMetric.id}`}
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  style={style}
                                >
                                  <IgPaper
                                    onMouseEnter={() => {
                                      setHover(index);
                                    }}
                                    onMouseLeave={() => {
                                      setHover(-1);
                                    }}
                                    onClick={
                                      isReadOnly
                                        ? undefined
                                        : () => {
                                            dispatch(
                                              initCustomEvent({
                                                customEvent:
                                                  customMetric.customEvent,
                                              })
                                            );
                                            dispatch(
                                              openDrawer({
                                                type: "customEvents",
                                              })
                                            );
                                          }
                                    }
                                    sx={{
                                      borderRadius: "5px",
                                      padding: "15px 22px",
                                      boxShadow: "none",
                                      "&:hover": {
                                        cursor: isReadOnly
                                          ? "default"
                                          : "pointer",
                                      },
                                    }}
                                    elevation={0}
                                  >
                                    <Grid
                                      container
                                      direction={"row"}
                                      alignItems={"center"}
                                      wrap={"nowrap"}
                                      justifyContent={"space-between"}
                                      sx={{ height: "47px" }}
                                    >
                                      <Grid
                                        alignItems={"center"}
                                        wrap={"nowrap"}
                                        container
                                        columnSpacing={1.2}
                                        direction={"row"}
                                        size={10.5}
                                      >
                                        <Grid {...dragProvided.dragHandleProps}>
                                          {isReadOnly ||
                                          hover !== index ||
                                          experienceCustomMetrics.length ===
                                            1 ? (
                                            <Box
                                              sx={{
                                                height: "24px",
                                                width: "24px",
                                              }}
                                            />
                                          ) : (
                                            <IgTooltip
                                              title={"Drag to reorder"}
                                            >
                                              <GripVertical />
                                            </IgTooltip>
                                          )}
                                        </Grid>
                                        <Grid
                                          container
                                          direction={"column"}
                                          wrap={"nowrap"}
                                        >
                                          <Grid>
                                            <IgTypography
                                              variant={"h3"}
                                              sx={{
                                                fontWeight: 600,
                                              }}
                                            >
                                              {customMetric.customEvent?.name}
                                            </IgTypography>
                                          </Grid>
                                          <Grid>
                                            <IgTypography
                                              sx={{
                                                color:
                                                  theme.palette.text.secondary,
                                              }}
                                            >
                                              {
                                                customMetric.customEvent
                                                  ?.description
                                              }
                                            </IgTypography>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                      {!isReadOnly &&
                                        hover === index &&
                                        !(
                                          experienceKeyMetrics.length <= 1 &&
                                          index === 0
                                        ) && (
                                          <Grid
                                            container
                                            direction={"row"}
                                            alignItems={"center"}
                                            wrap={"nowrap"}
                                            justifyContent={"end"}
                                            sx={{ width: "157px" }}
                                            size={1.5}
                                          >
                                            <Grid>
                                              <IgIconButton
                                                onClick={(e) =>
                                                  handleRemoveCustomMetric(
                                                    e,
                                                    index
                                                  )
                                                }
                                              >
                                                <X size={18} />
                                              </IgIconButton>
                                            </Grid>
                                          </Grid>
                                        )}
                                    </Grid>
                                  </IgPaper>
                                </Grid>
                              );
                            }}
                          </Draggable>
                        )
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Grid>
        {!isReadOnly && (
          <Grid container mt={experienceCustomMetrics.length ? 3 : 0}>
            <IgButton
              startIcon={<Plus size={18} color={theme.igIconColors.dark} />}
              onClick={() => {
                mpClickAddMetric(experience.id, "Custom Metric");
                dispatch(openDrawer({ type: "customEvents" }));
              }}
            >
              Add Custom Metric
            </IgButton>
          </Grid>
        )}
      </Stack>
      {!isReadOnly && isCustomDrawerOpen && (
        <CustomEventsDrawer renderLocation={"customMetric"} />
      )}
    </IgPaper>
  );
};
